import React, {
  memo,
  ReactElement,
  useEffect,
  useMemo,
  useState,
} from 'react';
import cn from 'classnames';
import VideocamIcon from '@mui/icons-material/Videocam';
import styles from './styles';
import { DrawingType } from '../graphicUploader/types';
import { useBlobFileStorage } from 'components/core/BlobFileStorage';
import { GeneratedExtensionIcon } from './fileExtensions/generatedExtensionIcon';
import useIsMobile from '../../../hooks/useIsMobile';
import { useGalleryContext } from '.';
import Skeleton from '@mui/material/Skeleton';
import CircularProgress from '@mui/material/CircularProgress';
import { WordThumbnail } from '../graphicUploader/thumbnails/wordThumbnail';
import { ExcelThumbnail } from '../graphicUploader/thumbnails/excelThumbnail';
import { PdfThumbnail } from '../graphicUploader/thumbnails/pdfThumbnail';
import {
  fileKindService,
  signedRequestService,
  thumbnailService,
} from 'services/fileService';
import { buildPreviewFetchKey } from './model';
import {
  resolveGalleryListRoute,
  resolveMiniatureDocumentRoute,
} from './fileKindRoutingModel';

const MINIATURE_SIZE = 104;

const SKELETON_CONTAINER_STYLE = {
  position: 'relative' as const,
  width: `${MINIATURE_SIZE}px`,
  height: `${MINIATURE_SIZE}px`,
  margin: '5px',
};

const PDF_LOADING_CONTAINER_STYLE = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  height: '100%',
  minHeight: `${MINIATURE_SIZE}px`,
};

const MINIATURE_TILE_OVERFLOW_STYLE = { overflow: 'hidden' as const };

function miniatureRotatedImageStyle(imageRotation = 0): React.CSSProperties {
  return {
    transform: `rotate(${imageRotation}deg)`,
    width: '100%',
    height: '100%',
    objectFit: 'contain',
  };
}

interface Props {
  uniqueKey: string;
  index: number;
  drawing: DrawingType;
  isVisible: boolean;
  isActive: boolean;
}

const _Miniature: React.FC<Props> = ({
  uniqueKey,
  index,
  drawing,
  isVisible,
  isActive,
}) => {
  const blobFileStorage = useBlobFileStorage();
  const isMobile = useIsMobile();
  const cs = styles({ isMobile });
  const { changeGallery, useDisplayedFiles, downloadFile } = useGalleryContext();

  const indexSelector: <T>(files: T[]) => T = useMemo(() => {
    return function fileAtIndexSelector<T>(files: T[]) {
      return files[index];
    };
  }, [index]);
  const [file] = useDisplayedFiles(indexSelector);

  const fileId = file._id || file.localId;
  const previewFetchKey = useMemo(() => buildPreviewFetchKey(file), [file]);

  useEffect(() => {
    if (!isVisible) {
      return;
    }

    if (
      file.loading === false &&
      signedRequestService.hasResolvedDownloadSource(file) &&
      fileKindService.hasUsablePreviewSource(file)
    ) {
      return;
    }

    downloadFile(file);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible, previewFetchKey, downloadFile]);

  const onClick = (): void => {
    changeGallery('slide', index);
  };

  const csActive = { [cs.active]: isActive };
  // An error belongs to the file version it happened for: a new version is tried again.
  const [fileWithError, setFileWithError] = useState<typeof file>();
  const [fileWithVideoPreviewError, setFileWithVideoPreviewError] = useState<typeof file>();
  const error = fileWithError === file;
  const videoPreviewError = fileWithVideoPreviewError === file;

  const renderSkeleton = (): ReactElement => (
    <div style={SKELETON_CONTAINER_STYLE}>
      <Skeleton
        className={cs.miniatureSkeleton}
        variant='rectangular'
        width={MINIATURE_SIZE}
        height={MINIATURE_SIZE}
      />
    </div>
  );

  if (file.loading && !fileKindService.hasUsablePreviewSource(file)) {
    return renderSkeleton();
  }

  const listRoute = resolveGalleryListRoute(file);

  if (listRoute === 'video') {
    const videoSource = fileKindService.resolveVideoPreviewSource(file);
    const videoMimeType = fileKindService.resolveVideoMimeType(file);

    if (videoSource && !videoPreviewError) {
      return (
        <div
          className={cn(cs.video, cs.miniatureImage, csActive)}
          onClick={onClick}
          style={MINIATURE_TILE_OVERFLOW_STYLE}
        >
          <video
            key={videoSource}
            crossOrigin='use-credentials'
            preload='metadata'
            muted
            playsInline
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
            onError={() => setFileWithVideoPreviewError(file)}
          >
            <source src={videoSource} type={videoMimeType} />
          </video>
        </div>
      );
    }

    return (
      <div className={cn(cs.video, csActive)} onClick={onClick}>
        <VideocamIcon />
      </div>
    );
  }

  if (listRoute === 'galleryImage') {
    const storedBlobUrl = thumbnailService.resolveStoredBlobUrl(
      drawing,
      file,
      blobFileStorage.getItem
    );

    const imageSrc = thumbnailService.resolveMiniatureSrc(
      error,
      storedBlobUrl,
      drawing,
      file
    );

    if (imageSrc === null) {
      return renderSkeleton();
    }

    return (
      <div
        className={cn(cs.video, cs.miniatureImage, csActive)}
        onClick={onClick}
        style={MINIATURE_TILE_OVERFLOW_STYLE}
      >
        <img
          crossOrigin='use-credentials'
          style={miniatureRotatedImageStyle(file.imageRotation)}
          key={`${fileId}-${imageSrc}`}
          src={imageSrc}
          alt={file.description || file.title}
          onError={() => setFileWithError(file)}
        />
      </div>
    );
  }

  const documentRoute = resolveMiniatureDocumentRoute(file);

  if (documentRoute === 'word' && file.downloadSrc) {
    return (
      <div
        className={cn(cs.video, csActive)}
        style={{ ...MINIATURE_TILE_OVERFLOW_STYLE, padding: 0 }}
      >
        <WordThumbnail
          src={file.downloadSrc}
          thumbnailSize={MINIATURE_SIZE}
          onClick={onClick}
          fileType={file.type}
          fileExtension={file.data?.extension}
        />
      </div>
    );
  }

  if (documentRoute === 'excel' && file.downloadSrc) {
    return (
      <div
        className={cn(cs.video, csActive)}
        style={{ ...MINIATURE_TILE_OVERFLOW_STYLE, padding: 0 }}
      >
        <ExcelThumbnail
          src={file.downloadSrc}
          thumbnailSize={MINIATURE_SIZE}
          onClick={onClick}
          fileType={file.type}
          fileExtension={file.data?.extension}
        />
      </div>
    );
  }

  if (documentRoute === 'pdfThumbnail') {
    const pdfThumbnailModel = fileKindService.resolvePdfThumbnailModel(file)!;

    if (pdfThumbnailModel.kind === 'loading') {
      return (
        <div className={cn(cs.video, csActive)} onClick={onClick}>
          <div style={PDF_LOADING_CONTAINER_STYLE}>
            <CircularProgress size={20} />
          </div>
        </div>
      );
    }

    return (
      <div
        className={cn(cs.video, csActive)}
        onClick={onClick}
        style={{ ...MINIATURE_TILE_OVERFLOW_STYLE, padding: 0 }}
      >
        <PdfThumbnail
          src={pdfThumbnailModel.src}
          thumbnailSize={MINIATURE_SIZE}
          onClick={onClick}
          fileType={file.type}
          fileExtension={file.data?.extension}
          lazy={!isVisible}
          thumbnailCacheKey={pdfThumbnailModel.cacheKey}
        />
      </div>
    );
  }

  return (
    <div className={cn(cs.video, csActive)} onClick={onClick}>
      <GeneratedExtensionIcon
        fileType={file.type}
        fileExtension={file.data?.extension}
        size={48}
      />
    </div>
  );
};

interface MiniatureTextProps {
  text?: string | null;
  isActive: boolean;
}

function _MiniatureText({ text, isActive }: MiniatureTextProps): ReactElement {
  const cs = styles({ isMobile: false });
  const csActive = { [cs.activeText]: isActive };
  return <p className={cn(cs.miniatureText, csActive)}>{text || ' '}</p>;
}

const Miniature = memo(_Miniature);
const MiniatureText = memo(_MiniatureText);

export { Miniature, MiniatureText };
